/* iter55g52 — Admin Booking Console.
 *
 * Read-only table listing every marketing booking ops needs to
 * hand-fulfil (BookBub Featured, Newsletter Swap, Podcast Interview).
 * Filterable by status. Reachable at `/admin/marketing-bookings`
 * — admin-only route wired in `App.js`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, Loader2, ExternalLink, MessageSquare, Save,
  Send, UserRound, Headset,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { adminMarketingApi } from '@/lib/api';

const usd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const STATUS_TABS = ['all', 'pending', 'paid', 'cancelled', 'failed'];

export default function AdminMarketingBookings() {
  const [rows, setRows]       = useState([]);
  const [counts, setCounts]   = useState({});
  const [loading, setLoad]    = useState(true);
  const [tab, setTab]         = useState('all');
  // iter55g53 — Inline note editing per booking.
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingId, setSaving]       = useState(null);
  // iter55h — Ops reply thread (per booking).
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyingId, setReplying]     = useState(null);
  // iter55j — @-mention autocomplete for the reply thread.
  const [teammates, setTeammates]     = useState([]);
  const [mentionCtx, setMentionCtx]   = useState(null);
  useEffect(() => {
    let alive = true;
    adminMarketingApi.teammates()
      .then((r) => { if (alive) setTeammates(r?.teammates || []); })
      .catch(() => { /* silent — mention picker just won't appear */ });
    return () => { alive = false; };
  }, []);

  const load = async () => {
    setLoad(true);
    try {
      const r = await adminMarketingApi.bookings(tab === 'all' ? undefined : tab);
      setRows(r.bookings || []);
      setCounts(r.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load bookings.');
    } finally { setLoad(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [tab]);

  const saveNote = async (bookingId, statusOverride) => {
    setSaving(bookingId);
    try {
      const patch = { ops_note: noteDrafts[bookingId] ?? '' };
      if (statusOverride) patch.status = statusOverride;
      await adminMarketingApi.patchBooking(bookingId, patch);
      toast.success(statusOverride ? `Booking marked ${statusOverride}` : 'Note saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(null); }
  };

  const sendReply = async (bookingId) => {
    const text = (replyDrafts[bookingId] || '').trim();
    if (!text) { toast.error('Type a reply first.'); return; }
    setReplying(bookingId);
    try {
      await adminMarketingApi.replyBooking(bookingId, text);
      setReplyDrafts((s) => ({ ...s, [bookingId]: '' }));
      toast.success('Reply sent to author');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reply failed.');
    } finally { setReplying(null); }
  };

  // Merge author + ops replies into a chronological thread.
  const buildThread = (b) => {
    const author = (b.author_replies || []).map((r) => ({ ...r, actor: 'author' }));
    const ops    = (b.ops_replies    || []).map((r) => ({ ...r, actor: 'admin'  }));
    return [...author, ...ops].sort((a, x) => (a.at || '').localeCompare(x.at || ''));
  };

  const totalPaid = useMemo(
    () => rows.filter((r) => r.status === 'paid')
              .reduce((s, r) => s + (Number(r.amount_paid_cents) || 0), 0),
    [rows]);

  return (
    <div className="w-full bg-background text-foreground">
      <header className="border-b border-border bg-panel-header/40 px-6 py-3">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => (window.location.href = '/app')}
                  data-testid="admin-bookings-back" className="gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-[15px] font-semibold">Marketing Bookings — Ops Console</h1>
            <div className="text-[11px] text-muted-foreground">
              Every managed-service booking. Hand-fulfil the BookBub / Newsletter Swap / Podcast queue.
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-6 py-6">
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setTab(s)}
              data-testid={`admin-bookings-tab-${s}`}
              className={
                'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11.5px] transition ' +
                (tab === s
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-card hover:border-primary/40')
              }
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              {counts[s] > 0 && (
                <span className="rounded-sm bg-muted px-1 text-[9.5px] font-mono">
                  {counts[s]}
                </span>
              )}
            </button>
          ))}
          <div className="ml-auto text-[11px] text-muted-foreground">
            Total paid volume: <span className="font-mono-stat font-semibold">{usd(totalPaid)}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <Card className="p-6 text-center text-[12px] text-muted-foreground">
            No bookings match this filter.
          </Card>
        ) : (
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-[12px]">
              <thead className="bg-panel-header/40">
                <tr className="text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Booked</th>
                  <th className="px-3 py-2">Author</th>
                  <th className="px-3 py-2">Platform</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Status</th>
                  <th className="px-3 py-2 text-right">Booking ID</th>
                  <th className="px-3 py-2 text-right">Stripe</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <React.Fragment key={b.id}>
                  <tr className="border-t border-border"
                      data-testid={`admin-booking-row-${b.id}`}>
                    <td className="px-3 py-2 font-mono-stat text-[10.5px] text-muted-foreground">
                      {fmtDate(b.paid_at || b.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{b.author?.name || '—'}</div>
                      <div className="text-[10.5px] text-muted-foreground">{b.author?.email}</div>
                    </td>
                    <td className="px-3 py-2 font-medium">{b.platform_name}</td>
                    <td className="px-3 py-2 text-right font-mono-stat">
                      {usd(b.amount_paid_cents || b.amount_cents)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={
                        'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider ' +
                        (b.status === 'paid'
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : b.status === 'fulfilled'
                          ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'
                          : b.status === 'pending'
                          ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-muted text-muted-foreground')
                      }>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-stat text-[10.5px] text-muted-foreground">
                      {b.id}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {b.checkout_session_id ? (
                        <a href={`https://dashboard.stripe.com/payments/${b.receipt_url || b.checkout_session_id}`}
                           target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                           data-testid={`admin-booking-stripe-${b.id}`}>
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                  <tr className="border-t border-border/40 bg-secondary/20">
                    <td className="px-3 pb-2 pt-1" colSpan={7}>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                        <Input
                          value={noteDrafts[b.id] ?? b.ops_note ?? ''}
                          onChange={(e) => setNoteDrafts((s) => ({ ...s, [b.id]: e.target.value }))}
                          placeholder="Ops note (e.g. 'BookBub slot confirmed for Nov 12')"
                          className="h-7 flex-1 text-[11.5px]"
                          data-testid={`admin-booking-note-${b.id}`}
                        />
                        <Button size="sm" variant="outline"
                                className="h-7 gap-1 text-[10.5px]"
                                onClick={() => saveNote(b.id)}
                                disabled={savingId === b.id}
                                data-testid={`admin-booking-note-save-${b.id}`}>
                          {savingId === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          Save
                        </Button>
                        {b.status !== 'fulfilled' && (
                          <Button size="sm"
                                  className="h-7 gap-1 text-[10.5px]"
                                  onClick={() => saveNote(b.id, 'fulfilled')}
                                  disabled={savingId === b.id}
                                  data-testid={`admin-booking-fulfil-${b.id}`}>
                            Mark fulfilled
                          </Button>
                        )}
                      </div>
                      {/* iter55h — Reply thread with the author. */}
                      {(() => {
                        const thread = buildThread(b);
                        return (
                          <div className="mt-2 space-y-1.5"
                               data-testid={`admin-booking-thread-${b.id}`}>
                            {thread.length === 0 ? (
                              <div className="text-[10.5px] italic text-muted-foreground">
                                No messages yet — the author will get an email when you reply.
                              </div>
                            ) : (
                              <ul className="space-y-1">
                                {thread.map((m, i) => {
                                  const isOps = m.actor === 'admin';
                                  return (
                                    <li key={`${b.id}-msg-${i}`}
                                        className={
                                          'flex items-start gap-1.5 rounded-sm border px-2 py-1 text-[11px] ' +
                                          (isOps
                                            ? 'ml-6 border-primary/40 bg-primary/5'
                                            : 'mr-6 border-border bg-card')
                                        }
                                        data-testid={`admin-booking-thread-msg-${b.id}-${i}`}>
                                      {isOps
                                        ? <Headset   className="mt-[2px] h-3 w-3 flex-shrink-0 text-primary" />
                                        : <UserRound className="mt-[2px] h-3 w-3 flex-shrink-0 text-muted-foreground" />}
                                      <div className="flex-1">
                                        <div className="flex items-baseline gap-2">
                                          <span className="text-[9.5px] font-semibold uppercase tracking-wider">
                                            {isOps ? 'Ops' : (b.author?.name || 'Author')}
                                          </span>
                                          <span className="font-mono-stat text-[9.5px] text-muted-foreground">
                                            {fmtDate(m.at)}
                                          </span>
                                        </div>
                                        <div className="whitespace-pre-wrap text-[11.5px]">{m.text}</div>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            <div className="relative flex items-center gap-2">
                              <Send className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                              <Input
                                value={replyDrafts[b.id] || ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setReplyDrafts((s) => ({ ...s, [b.id]: v }));
                                  // iter55j — @-mention autocomplete: open picker
                                  // when the caret sits inside a `@partial` token.
                                  const caret = e.target.selectionStart ?? v.length;
                                  const upto = v.slice(0, caret);
                                  const m = upto.match(/@([\w.\-]*)$/);
                                  if (m) setMentionCtx({ bookingId: b.id, prefix: m[1] });
                                  else if (mentionCtx && mentionCtx.bookingId === b.id) setMentionCtx(null);
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') sendReply(b.id); }}
                                placeholder="Reply to author. Type @ to mention a teammate."
                                className="h-7 flex-1 text-[11.5px]"
                                data-testid={`admin-booking-reply-input-${b.id}`}
                              />
                              <Button size="sm" variant="outline"
                                      className="h-7 gap-1 text-[10.5px]"
                                      onClick={() => sendReply(b.id)}
                                      disabled={replyingId === b.id || !(replyDrafts[b.id] || '').trim()}
                                      data-testid={`admin-booking-reply-send-${b.id}`}>
                                {replyingId === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                                Reply
                              </Button>
                              {mentionCtx && mentionCtx.bookingId === b.id && (() => {
                                const prefix = (mentionCtx.prefix || '').toLowerCase();
                                const matches = teammates
                                  .filter((n) => n.toLowerCase().includes(prefix))
                                  .slice(0, 6);
                                if (matches.length === 0) return null;
                                return (
                                  <div
                                    className="absolute left-5 top-8 z-10 w-56 rounded-md border border-border bg-card shadow-lg"
                                    data-testid={`admin-booking-mention-picker-${b.id}`}
                                  >
                                    <div className="border-b border-border px-2 py-1 text-[9.5px] uppercase tracking-wider text-muted-foreground">
                                      Mention a teammate
                                    </div>
                                    {matches.map((name) => (
                                      <button
                                        key={name}
                                        type="button"
                                        onClick={() => {
                                          const current = replyDrafts[b.id] || '';
                                          const replaced = current.replace(/@([\w.\-]*)$/, `@${name} `);
                                          setReplyDrafts((s) => ({ ...s, [b.id]: replaced }));
                                          setMentionCtx(null);
                                        }}
                                        data-testid={`admin-booking-mention-pick-${b.id}-${name}`}
                                        className="flex w-full items-center gap-2 px-2 py-1 text-left text-[11.5px] hover:bg-primary/10"
                                      >
                                        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary/20 text-[9px] font-semibold text-primary">
                                          {name.charAt(0).toUpperCase()}
                                        </span>
                                        <span>{name}</span>
                                      </button>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
